import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@ca/environment/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, Observable, Subject } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import * as FileUtils from '../../../utils/file/file-utils';
import { FilePreviewerDialogComponent } from '../../shared/components/file-previewer-dialog/file-previewer-dialog.component';
import { MedResultItemVM } from '../models';
import { FileModel } from '../models/file-model.model';
import { FilePreview } from '../models/file-preview.model';
import { FilePreviewTypes } from './../models/file-preview.type';
import { ArivisService } from './arivis.service';
import { MachineLearningService } from './machine-learning.service';
import { UserNotificationService } from './user-notification.service';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root',
})
export class FilePreviewService {
    private inlineContent$$ = new Subject<FilePreview>();

    get inlineContent$(): Observable<FilePreview> {
        return this.inlineContent$$.asObservable();
    }

    constructor(
        private arivisService: ArivisService,
        private http: HttpClient,
        private ngbModal: NgbModal,
        private userService: UserService,
        private userNotificationService: UserNotificationService,
        private machineLearningService: MachineLearningService,
    ) {}

    isAtLeastOneArivisPreviewableFileSelected(files: (FileModel | MedResultItemVM)[]): boolean {
        return files.some((file: FileModel | MedResultItemVM) => file.type && this.arivisService.isSupportedByArivis(file.type));
    }

    /**
     * Fetches the file content (from a URL) and opens it. The content will either be put into a modal
     * or displayed inline (in the runner).
     */
    openWithURL(url: string, type?: string, openContentIn?: 'modal' | 'inline' | 'newTab'): void {
        if (type === null) {
            type = FileUtils.getFileExtension(url);
        }

        if (openContentIn == null) {
            if (type === 'html' || type === 'htm') {
                openContentIn = 'newTab';
            } else {
                openContentIn = 'modal';
            }
        }

        this.userNotificationService.showSpinner();

        // Fetch file content
        from(this.getFileContent(url, type || '')).subscribe(
            (fileContent: string) => {
                this.userNotificationService.hideSpinner();

                if (openContentIn === 'newTab') {
                    const newWindow: Window | null = window.open();
                    if (newWindow != null) {
                        newWindow.document.open();
                        newWindow.document.title = url;
                        newWindow.document.write(fileContent as string);
                        newWindow.document.close();
                    }
                } else {
                    if (openContentIn === 'modal') {
                        this.openFilePreviewerModal(fileContent, FileUtils.getFileNameWithExtension(url), type);
                    } else if (openContentIn === 'inline') {
                        // Inline view is only implemented for HTML files so far.
                        // Emit event, which is caught by html-preview-frame.component
                        if (type != null) {
                            this.inlineContent$$.next({ content: fileContent, type: type });
                        }
                    }
                }
            },
            (err: unknown) => {
                this.userNotificationService.hideSpinner();
                console.error('Error fetching file content for previewer', err);
            },
        );
    }
    openFilePreviewerModal(fileContent: string, fileName?: string, type?: string): void {
        const filePreviewer = this.ngbModal.open(FilePreviewerDialogComponent, { size: 'fullscreen' });
        const component = <FilePreviewerDialogComponent>filePreviewer.componentInstance;
        component.fileName = fileName;
        component.type = type;
        component.data = fileContent;
    }

    /**
     * Generates an URL to open Arivis Machine Learning view. Can open the viewer with one or several images.
     */
    generateArivisMachineLearningViewerUrl(file: FileModel | FileModel[], dataSetId?: string): string {
        const fileIdString: string = this.arivisService.getFileIdsFromOneOrSeveralFiles(file);
        if (dataSetId != null) {
            return `${environment.arivisUrlPrefix}${this.arivisService.arivisViewerUrlPart}imageid=${fileIdString}&datasetId=${dataSetId}&mlwf`;
        }
        return `${environment.arivisUrlPrefix}${this.arivisService.arivisViewerUrlPart}imageid=${fileIdString}&mlwf`;
    }

    generateArivisMachineLearningViewerUrlForDataset$(datasetId: string, userId?: string | null): Observable<string | null> {
        if (userId) {
            return this.generateViewerUrlForUserId$(datasetId, userId);
        } else {
            return this.generateViewerUrlForDatasetId$(datasetId);
        }
    }

    generateArivisMachineLearningOpenResult(workflowRunId: string): string {
        return `${environment.arivisUrlPrefix}${this.arivisService.arivisViewerUrlPart}workflowRunId=${workflowRunId}`;
    }

    canBeOpenedInNewTab(url: string | undefined): boolean {
        if (url == null || url.trim() === '') {
            return false;
        }
        const type: string | undefined = FileUtils.getFileExtension(url);
        if ((type != null && this.arivisService.isSupportedByArivis(type)) || type === 'html' || type === 'htm') {
            return true;
        }
        return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    private async getFileContent(url: string, type: FilePreviewTypes | string): Promise<string> {
        let data;
        switch (type) {
            case 'txt':
            case 'csv':
            case 'html':
            case 'htm':
                data = await this.http.get(url, { responseType: 'text' }).toPromise();

                // If it's a csv, we need to parse the file before returning it
                if (type === 'csv') {
                    data = await this.parseCSV(data);
                }
                break;
            case 'json':
                data = await this.http.get(url, { responseType: 'json' }).toPromise();

                break;
        }

        return data;
    }

    private async parseCSV(data: string): Promise<string> {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const csv = require('csvtojson');
        return csv().fromString(data);
    }

    private generateViewerUrlForUserId$(datasetId: string, userId: string): Observable<string | null> {
        return this.userService.getArivisViewer$(userId).pipe(map(res => `${environment.arivisUrlPrefix}/${res.viewerType}/?datasetId=${datasetId}&mlwf`));
    }

    private generateViewerUrlForDatasetId$(datasetId: string): Observable<string | null> {
        return this.machineLearningService.fetchDatasetById$(datasetId).pipe(
            mergeMap(dataset => {
                const originalOwnerUserId = dataset.userId;
                return this.userService
                    .getArivisViewer$(originalOwnerUserId || '')
                    .pipe(map(res => (res ? `${environment.arivisUrlPrefix}/${res.viewerType}/?datasetId=${datasetId}&mlwf` : null)));
            }),
        );
    }
}
